/**
 * Alternative Products Widget
 * Shows similar in-stock products when parent product is out of stock
 *
 * Features:
 * - Auto-shows only when parent product is unavailable
 * - Fetches recommendations from Shopify API
 * - Filters out out-of-stock products
 * - Beautiful carousel design
 * - Quick view/add functionality
 * - Fallback to collection/tag-based alternatives
 *
 * @version 1.0.0
 */

(function(window) {
  'use strict';

  class AlternativesWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      // Widget state
      this.alternatives = [];
      this.loadingProducts = false;
      this.maxProducts = config.maxProducts || 8;

      // Content settings with defaults
      this.content = {
        heading: config.heading || "Oh No! This One's Sold Out",
        subheading: config.subheading || "But don't worry - these brilliant alternatives are ready to ship!",
        inStockBadge: config.inStockBadge || 'IN STOCK ✓',
        viewProductButton: config.viewProductButton || 'View Details',
        quickAddButton: config.quickAddButton || 'Quick Add',
        noAlternativesHeading: config.noAlternativesHeading || 'Temporarily Out of Stock',
        noAlternativesMessage: config.noAlternativesMessage || 'This product is currently unavailable. Check back soon or browse our full collection!',
        browseCollectionButton: config.browseCollectionButton || 'Browse Collection',
        loadingMessage: config.loadingMessage || 'Finding great alternatives...'
      };
    }

    /**
     * Initialize widget - check parent stock and load alternatives
     */
    async init() {
      // Check if parent product is out of stock
      const parentStockCheck = this.checkParentStock();

      if (parentStockCheck.available) {
        // Don't show this widget if parent has stock
        this.container.style.display = 'none';
        // Also hide the parent wrapper to prevent empty space
        if (this.container.parentElement && this.container.parentElement.classList.contains('pw-widget')) {
          this.container.parentElement.style.display = 'none';
        }
        return;
      }

      // Load alternative products
      await this.loadAlternativeProducts();
    }

    /**
     * Check if parent product has stock available
     * @returns {{available: boolean, message: string}}
     */
    checkParentStock() {
      if (!this.productData || !this.productData.variants) {
        return {
          available: true, // Default to available if no data
          message: 'No variant data available'
        };
      }

      const { variants } = this.productData;

      const hasStock = variants.some(variant => {
        if (variant.available === true) return true;
        if (variant.inventory_policy === 'continue') return true;
        if (!variant.inventory_management) return true;
        if (variant.inventory_quantity > 0) return true;
        return false;
      });

      return {
        available: hasStock,
        message: hasStock ? 'Parent has stock' : 'Parent out of stock'
      };
    }

    /**
     * Load alternative products from various sources
     */
    async loadAlternativeProducts() {
      this.loadingProducts = true;

      try {
        // Strategy 1: Shopify Recommendations API (AI-powered)
        const recommendations = await this.fetchRecommendations();

        if (recommendations.length > 0) {
          this.alternatives = recommendations;
          return;
        }

        // Strategy 2: Fallback to collection-based alternatives
        // For now, we'll just use recommendations
        // Could extend to fetch from collection if needed

      } catch (error) {
        console.error('[AlternativesWidget] Error loading alternatives:', error);
        this.alternatives = [];
      } finally {
        this.loadingProducts = false;
      }
    }

    /**
     * Fetch product recommendations from Shopify
     */
    async fetchRecommendations() {
      if (!this.productData || !this.productData.id) {
        console.warn('[AlternativesWidget] No product ID available');
        return [];
      }

      try {
        const response = await fetch(`/recommendations/products.json?product_id=${this.productData.id}&limit=${this.maxProducts}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const products = data.products || [];

        // Filter to only products with stock
        const inStockProducts = products.filter(product => {
          // Check if product has ANY variant in stock
          return product.variants && product.variants.some(variant => {
            if (variant.available === true) return true;
            if (variant.inventory_policy === 'continue') return true;
            if (!variant.inventory_management) return true;
            if (variant.inventory_quantity > 0) return true;
            return false;
          });
        });

        return inStockProducts;

      } catch (error) {
        console.error('[AlternativesWidget] Error fetching recommendations:', error);
        return [];
      }
    }

    /**
     * Render widget to container
     */
    render() {
      // CRITICAL: Don't render if parent product has stock
      const parentStockCheck = this.checkParentStock();
      if (parentStockCheck.available) {
        this.container.style.display = 'none';
        // Also hide the parent wrapper to prevent empty space
        if (this.container.parentElement && this.container.parentElement.classList.contains('pw-widget')) {
          this.container.parentElement.style.display = 'none';
        }
        return;
      }

      if (this.loadingProducts) {
        return;
      }

      const html = this.buildWidgetHTML();
      this.setContent(html);
      this.attachEventListeners();

      this.emit('alternatives:rendered');
    }

    /**
     * Build widget HTML
     */
    buildWidgetHTML() {
      if (this.alternatives.length === 0) {
        return this.buildNoAlternativesHTML();
      }

      return `
        <div class="pw-widget--card pw-alternatives">
          ${this.buildHeader()}
          ${this.buildProductCarousel()}
        </div>
      `;
    }

    /**
     * Build widget header
     */
    buildHeader() {
      return `
        <div class="pw-alternatives__header" style="text-align: center; margin-bottom: 1.5rem;">
          <h3 class="pw-text-2xl pw-font-bold pw-mb-2" style="color: rgb(var(--color-drawer-text));">
            ${this.content.heading}
          </h3>
          <p class="pw-text-base" style="color: rgb(var(--color-drawer-text) / 0.7);">
            ${this.content.subheading}
          </p>
        </div>
      `;
    }

    /**
     * Build product carousel (using theme's carousel pattern)
     */
    buildProductCarousel() {
      return `
        <div class="pw-alternatives-carousel-wrapper" role="region" aria-label="Alternative products carousel">
          <div class="pw-alternatives-carousel" data-carousel style="
            display: flex;
            gap: 1rem;
            overflow-x: auto;
            overflow-y: hidden;
            scroll-snap-type: x mandatory;
            padding-bottom: 1rem;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: thin;
          ">
            ${this.alternatives.map((product, index) => this.buildProductCard(product, index)).join('')}
          </div>
        </div>
      `;
    }

    /**
     * Build individual product card
     */
    buildProductCard(product, index) {
      const price = this.formatPrice(product.price);
      const comparePrice = product.compare_at_price ? this.formatPrice(product.compare_at_price) : null;
      const imageUrl = product.featured_image || product.images?.[0];
      const hasOnlyOneVariant = product.variants && product.variants.length === 1;

      return `
        <div class="pw-alternative-card" style="
          flex: 0 0 280px;
          scroll-snap-align: start;
          background: white;
          border-radius: var(--rounded-card);
          border: 1px solid rgb(var(--color-drawer-text) / 0.1);
          overflow: hidden;
          transition: transform 0.2s, box-shadow 0.2s;
        " data-product-index="${index}">

          <!-- Product Image -->
          <div style="position: relative; aspect-ratio: 1/1; background: rgb(var(--color-drawer-text) / 0.03);">
            ${imageUrl ? `
              <img
                src="${imageUrl}"
                alt="${this.escapeHtml(product.title)}"
                loading="lazy"
                style="width: 100%; height: 100%; object-fit: cover;"
              />
            ` : ''}

            <!-- In Stock Badge -->
            <div style="
              position: absolute;
              top: 0.75rem;
              right: 0.75rem;
              background: rgb(34, 197, 94);
              color: white;
              padding: 0.375rem 0.75rem;
              border-radius: 9999px;
              font-size: 0.75rem;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.025em;
            ">
              ${this.content.inStockBadge}
            </div>
          </div>

          <!-- Product Info -->
          <div style="padding: 1rem;">
            <h4 style="
              font-size: 0.875rem;
              font-weight: 600;
              margin-bottom: 0.5rem;
              line-height: 1.4;
              color: rgb(var(--color-drawer-text));
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            ">
              ${this.escapeHtml(product.title)}
            </h4>

            <!-- Price -->
            <div style="margin-bottom: 0.75rem;">
              ${comparePrice ? `
                <span style="
                  font-size: 0.875rem;
                  text-decoration: line-through;
                  color: rgb(var(--color-drawer-text) / 0.5);
                  margin-right: 0.5rem;
                ">
                  ${comparePrice}
                </span>
              ` : ''}
              <span style="
                font-size: 1.125rem;
                font-weight: 700;
                color: rgb(var(--color-drawer-text));
              ">
                ${price}
              </span>
            </div>

            <!-- CTA Button -->
            <a
              href="${product.url}"
              class="pw-btn pw-btn--secondary pw-btn--sm pw-w-full"
              style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                padding: 0.625rem 1rem;
                background: rgb(var(--color-base-button-background));
                color: rgb(var(--color-base-button-text));
                border-radius: var(--rounded-button);
                font-size: 0.875rem;
                font-weight: 600;
                text-decoration: none;
                transition: opacity 0.2s;
              "
              onmouseover="this.style.opacity='0.9'"
              onmouseout="this.style.opacity='1'"
            >
              ${this.content.viewProductButton}
            </a>
          </div>
        </div>
      `;
    }

    /**
     * Build "no alternatives" message
     */
    buildNoAlternativesHTML() {
      return `
        <div class="pw-widget--card pw-alternatives pw-alternatives--empty" style="text-align: center; padding: 2rem;">
          <div style="
            max-width: 400px;
            margin: 0 auto;
            color: rgb(var(--color-drawer-text));
          ">
            <h3 class="pw-text-xl pw-font-bold pw-mb-2">
              ${this.content.noAlternativesHeading}
            </h3>
            <p class="pw-text-base pw-mb-4" style="color: rgb(var(--color-drawer-text) / 0.7);">
              ${this.content.noAlternativesMessage}
            </p>
            ${this.buildBrowseCollectionButton()}
          </div>
        </div>
      `;
    }

    /**
     * Build browse collection button
     */
    buildBrowseCollectionButton() {
      // Try to get collection URL from product data
      const collectionUrl = this.productData?.collections?.[0]?.url || '/collections/all';

      return `
        <a
          href="${collectionUrl}"
          class="pw-btn pw-btn--primary"
          style="
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.75rem 1.5rem;
            background: rgb(var(--color-base-button-background));
            color: rgb(var(--color-base-button-text));
            border-radius: var(--rounded-button);
            font-size: 0.875rem;
            font-weight: 600;
            text-decoration: none;
          "
        >
          ${this.content.browseCollectionButton}
        </a>
      `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Add hover effects to product cards
      const cards = this.container.querySelectorAll('.pw-alternative-card');
      cards.forEach(card => {
        card.addEventListener('mouseenter', () => {
          card.style.transform = 'translateY(-4px)';
          card.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
        });
        card.addEventListener('mouseleave', () => {
          card.style.transform = 'translateY(0)';
          card.style.boxShadow = 'none';
        });
      });
    }

    /**
     * Format price in pounds
     */
    formatPrice(priceInPence) {
      const pounds = priceInPence / 100;
      return `£${pounds.toFixed(2)}`;
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  // Register widget with ProductWidgets system
  if (window.ProductWidgets && window.ProductWidgets.widgets) {
    window.ProductWidgets.widgets.AlternativesWidget = AlternativesWidget;
  } else {
    console.error('[ProductWidgets] ProductWidgets system not available');
  }

})(window);
